import type {
  ApolloClient,
  DocumentNode,
  ErrorPolicy,
  FetchPolicy,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client'
import { createQueryPreloader, type PreloadedQueryRef } from '@apollo/client/react'

/** Apollo Client interface - any client with a query method.
 * Uses FetchPolicy from Apollo (query() does not support cache-and-network). */
type ApolloClientLike = {
  query: <TData = unknown>(options: {
    query: DocumentNode
    variables?: OperationVariables
    fetchPolicy?: FetchPolicy
    errorPolicy?: ErrorPolicy
  }) => Promise<{ data?: TData }>
  readQuery?: <TData = unknown>(options: { query: DocumentNode; variables?: OperationVariables }) => TData | null
}

/**
 * Options for queryAndPreload - matches the second parameter of preloadQuery.
 * Variables are required when the query has variables.
 */
export type QueryAndPreloadOptions<TVariables extends OperationVariables = OperationVariables> = {
  variables?: TVariables
  fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only' | 'no-cache'
  errorPolicy?: ErrorPolicy
  [key: string]: unknown
}

/**
 * Type for the preloadQuery function from router context (Apollo TanStack Start integration).
 * Returns TransportedQueryRef which is serializable for TanStack Router.
 */
export type PreloadQueryFn = <TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryAndPreloadOptions<TVariables>
) => unknown

/**
 * Router (or test) context carrying Apollo client and preloadQuery for loaders.
 */
export type QueryAndPreloadContext = {
  apolloClient: ApolloClientLike
  preloadQuery: PreloadQueryFn
}

/**
 * Result of queryAndPreload: Apollo result data plus an optional preloaded query ref (for Suspense APIs).
 */
export type QueryAndPreloadResult<TData = unknown> = {
  /** `data` from `apolloClient.query` (typed as `TData`). */
  queryData: TData | undefined
  /** Transported query ref from preloadQuery for `useReadQuery`. */
  queryRef: unknown
}

/**
 * Loads a route query so `useReadQuery` renders without suspending, and returns its data for `head` and redirects.
 *
 * **SSR (`preloadQuery` from `createTransportedQueryPreloader`):** the preload always goes to the network
 * so its response can be streamed to the browser, so it is the only request: it is awaited and
 * `queryData` is read from the cache it filled (see {@link preloadOnServer}). `no-cache` keeps the
 * client path because its result never reaches the cache.
 *
 * **Client:** runs `apolloClient.query` first to populate the cache, then `preloadQuery` reads from it,
 * so client navigation does not show the route pending component. `apolloClient.query` uses `cache-first`
 * so the loader does not block on the network when the cache is warm. If that cache already had a complete result, **preloadQuery**
 * defaults to `cache-and-network` so `useReadQuery` paints cached data immediately and then
 * revalidates. On a cache miss, preload stays `cache-first` to avoid a second network request
 * after `query()` already fetched.
 *
 * @param context - Router context with `apolloClient` and `preloadQuery`
 * @param query - The GraphQL query document (TypedDocumentNode or DocumentNode)
 * @param options - Passed to `preloadQuery` (variables; preload `fetchPolicy` resolved by {@link preloadFetchPolicyAfterWarmCache})
 * @param fetchPolicy - `fetchPolicy` for the client's `apolloClient.query` only (defaults to `cache-first`)
 * @returns `{ queryData, queryRef }` — the query's data, plus the ref from `preloadQuery`
 */
export async function queryAndPreload<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  context: QueryAndPreloadContext,
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryAndPreloadOptions<TVariables>,
  fetchPolicy?: 'cache-first' | 'network-only' | 'no-cache'
): Promise<QueryAndPreloadResult<TData>> {
  const { apolloClient, preloadQuery } = context
  const variables = options?.variables
  const errorPolicy = options?.errorPolicy

  if (typeof window === 'undefined' && options?.fetchPolicy !== 'no-cache') {
    return preloadOnServer(context, query, options)
  }

  const hadCachedData = cacheHasCompleteResult(apolloClient, query, variables)

  const queryResult = await apolloClient.query<TData>({
    query,
    ...(variables !== undefined && { variables }),
    fetchPolicy: fetchPolicy ?? 'cache-first',
    ...(errorPolicy !== undefined && { errorPolicy }),
  })

  const queryRef = preloadQuery(query, {
    ...options,
    ...(variables !== undefined && { variables }),
    fetchPolicy: preloadFetchPolicyAfterWarmCache(options?.fetchPolicy, hadCachedData),
  })

  return {
    queryData: queryResult.data,
    queryRef,
  }
}

/**
 * SSR path: the transported preloader always sends its request to the network so the response can be
 * streamed to the browser, so a warm-up `apolloClient.query` would be a second identical request.
 * Awaiting the preloaded ref instead leaves the cache filled before render (no Suspense fallback on
 * the server) and still rejects on errors like `apolloClient.query` does.
 */
async function preloadOnServer<TData, TVariables extends OperationVariables>(
  { apolloClient, preloadQuery }: QueryAndPreloadContext,
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryAndPreloadOptions<TVariables>
): Promise<QueryAndPreloadResult<TData>> {
  const variables = options?.variables
  const queryRef = preloadQuery(query, {
    ...options,
    ...(variables !== undefined && { variables }),
    fetchPolicy: preloadFetchPolicyAfterWarmCache(options?.fetchPolicy, false),
  })

  await createQueryPreloader(apolloClient as unknown as ApolloClient).toPromise(queryRef as PreloadedQueryRef<TData>)

  return {
    queryData: apolloClient.readQuery?.<TData>({ query, ...(variables !== undefined && { variables }) }) ?? undefined,
    queryRef,
  }
}

function cacheHasCompleteResult(
  apolloClient: ApolloClientLike,
  query: DocumentNode,
  variables?: OperationVariables
): boolean {
  if (typeof apolloClient.readQuery !== 'function') {
    return false
  }

  try {
    const cached = apolloClient.readQuery({
      query,
      ...(variables !== undefined && { variables }),
    })
    return cached != null
  } catch {
    return false
  }
}

/**
 * Default `fetchPolicy` for `preloadQuery` after `apolloClient.query` has warmed the cache.
 * On SSR, {@link https://github.com/apollographql/apollo-client-integrations | `@apollo/client-react-streaming`}
 * only keeps `cache-and-network` when `prepareForReuse` is true; other policies become `network-only`.
 * On the client, a warm cache uses `cache-and-network` so revisiting a route revalidates without
 * blocking the loader on the network.
 */
function preloadFetchPolicyAfterWarmCache(
  explicit: QueryAndPreloadOptions['fetchPolicy'] | undefined,
  hadCachedData: boolean
): 'cache-first' | 'cache-and-network' | 'network-only' | 'no-cache' {
  if (explicit === 'network-only' || explicit === 'no-cache') {
    return explicit
  }

  if (typeof window === 'undefined') {
    /*
    SSR (typeof window === 'undefined'): default cache-and-network for preloadQuery, 
    because that is the only policy this code path keeps when prepareForReuse is true 
    (so it is not forced to network-only).
    */
    return 'cache-and-network'
  }

  if (explicit) {
    return explicit
  }

  return hadCachedData ? 'cache-and-network' : 'cache-first'
}
